import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { StoreService } from 'src/app/services/store.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-grn-print',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatProgressBar],
  templateUrl: './grn-print.component.html',
  styleUrl: './grn-print.component.scss'
})
export class GrnPrintComponent implements OnInit, OnDestroy {
  grnId!: number;
  grnData: any;
  previewData: any = null;
  isDialogMode = false;
  includeSignature = false;
  pdfDoc: any;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private storeService: StoreService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<GrnPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.grnId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.grnId) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.download(`GRN-${this.previewData?.grnCode || this.grnId}.pdf`);
  }

  async sendMail() {
    this.snackBar.open('GRN mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadData(): void {
    this.storeService.getGRNPrintById(this.grnId).subscribe({
      next: (data: any) => {
        this.grnData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.grnData || {});
        this.ensurePdfDoc();
      },
      error: (err) => console.error('Error loading GRN print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const warehouse = this.pickEntity(source, ['warehouse', 'whmaster']);
    const vendor = this.pickEntity(source, ['vendor', 'party', 'partymaster']);
    const fromBranch = this.pickEntity(source, ['frombranch', 'materialtransferbranch', 'branch']);
    const itemGroups = this.extractGroups(source);
    const items = itemGroups.flatMap((group: any) =>
      (group.items || []).map((item: any, index: number) => ({
        sr: 0,
        sourceCode: this.safeValue(group.code, ''),
        sourceDate: this.formatDateValue(group.date),
        productName: this.safeValue(item.productname || item.itemname || item.description),
        uom: this.safeValue(item.uomname || item.uom),
        challanQty: this.formatNumber(item.challanqty || 0),
        pendingQty: this.formatNumber(item.pendingrnqty || 0),
        grnQty: this.formatNumber(item.quantity || 0),
        billQty: this.formatNumber(item.billqty || 0),
        rate: this.formatCurrency(item.productrate || 0, false),
        amount: this.formatCurrency(item.amount || ((Number(item.quantity || 0) * Number(item.productrate || 0))), true),
        remarks: this.safeValue(item.remarks, '')
      }))
    ).map((item: any, index: number) => ({ ...item, sr: index + 1 }));

    const totalQty = items.reduce((sum: number, item: any) => sum + Number(String(item.grnQty).replace(/,/g, '')), 0);
    const totalAmount = Number(source.grnamt || 0);
    const typeId = Number(source.grntype || 0);

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      grnCode: this.safeValue(source.grncode),
      grnDate: this.formatDateValue(source.grndate),
      grnTypeLabel: typeId === 2 ? 'Transfer GRN' : typeId === 1 ? 'Purchase GRN' : 'Other GRN',
      warehouseName: this.safeValue(warehouse.name || warehouse.whname || source.warehousename),
      vendorName: this.safeValue(vendor.vendorname || vendor.name || source.vendorname, ''),
      fromBranchName: this.safeValue(fromBranch.branchname || source.frombranchname, ''),
      challanNo: this.safeValue(source.challanno, ''),
      challanDate: this.formatDateValue(source.challandate),
      partyDocNo: this.safeValue(source.partydocno, ''),
      partyDocDate: this.formatDateValue(source.partydocdate),
      transporterMode: this.safeValue(source.trptypename || source.trptype, ''),
      transporterName: this.safeValue(source.trpname, ''),
      transporterDocNo: this.safeValue(source.trpdocno, ''),
      transporterDocDate: this.formatDateValue(source.trpdocdate),
      vehicleNo: this.safeValue(source.vehicleno, ''),
      remarks: this.safeValue(source.remarks, ''),
      sourceGroups: itemGroups,
      items,
      totalItems: items.length,
      totalQty: this.formatNumber(totalQty),
      grnAmount: this.formatCurrency(totalAmount, true),
      amountInWords: `${toWords.convert(totalAmount, { currency: true })}/-`
    };
  }

  private ensurePdfDoc() {
    if (!this.previewData) {
      return;
    }

    const body = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'SOURCE', style: 'tableHeader' },
        { text: 'ITEM', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'CHALLAN', style: 'tableHeader' },
        { text: 'PENDING', style: 'tableHeader' },
        { text: 'GRN QTY', style: 'tableHeader' },
        { text: 'BILL QTY', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.items.map((item: any) => ([
        { text: String(item.sr), alignment: 'center' },
        { text: item.sourceCode ? `${item.sourceCode}\n${item.sourceDate}` : '-', fontSize: 8.2, alignment: 'center' },
        { text: item.productName },
        { text: item.uom, alignment: 'center' },
        { text: item.challanQty, alignment: 'right' },
        { text: item.pendingQty, alignment: 'right' },
        { text: item.grnQty, alignment: 'right', bold: true },
        { text: item.billQty, alignment: 'right' },
        { text: item.rate, alignment: 'right' },
        { text: item.amount, alignment: 'right', bold: true }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: 'Generated using VCS ERP',
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? {
            image: this.previewData.logo,
            alignment: 'center',
            width: 300,
            height: 300,
            opacity: 0.05,
            margin: [0, 190, 0, 0]
          }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [150, '*', 150],
            body: [[
              this.previewData.logo
                ? {
                    stack: [{ image: this.previewData.logo, fit: [76, 76], alignment: 'center', margin: [0, 4, 0, 0] }],
                    border: [false, false, false, false]
                  }
                : {
                    text: this.previewData.companyInitials || 'GR',
                    style: 'logoFallback',
                    border: [false, false, false, false]
                  },
              {
                stack: [
                  { text: this.previewData.companyName, style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((meta: string) => ({ text: meta, style: 'companyMeta' }))
                ].filter(Boolean),
                margin: [0, 3, 0, 0],
                border: [false, false, false, false]
              },
              {
                stack: [
                  { text: 'GRN DOCUMENT', style: 'badge' },
                  { text: `GRN Code: ${this.previewData.grnCode}`, style: 'meta' },
                  { text: `GRN Date: ${this.previewData.grnDate}`, style: 'meta' },
                  { text: `GRN Type: ${this.previewData.grnTypeLabel}`, style: 'meta' },
                  this.previewData.challanNo ? { text: `Challan No: ${this.previewData.challanNo}`, style: 'meta' } : ''
                ].filter(Boolean),
                fillColor: '#eef6ff',
                margin: [2, 6, 0, 0],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
            { width: '*', text: `Warehouse: ${this.previewData.warehouseName || '-'}`, style: 'summaryBox' },
            { width: '*', text: `Items: ${this.previewData.totalItems}`, style: 'summaryBox' },
            { width: '*', text: `GRN Qty: ${this.previewData.totalQty}`, style: 'summaryBox' },
            { width: '*', text: `GRN Amount: ${this.previewData.grnAmount}`, style: 'summaryBox' }
          ],
          columnGap: 8,
          margin: [0, 12, 0, 10]
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: this.previewData.grnTypeLabel === 'Transfer GRN' ? 'From Branch' : 'Vendor / Party', style: 'sectionHeader' },
                this.previewData.grnTypeLabel === 'Transfer GRN'
                  ? { text: this.previewData.fromBranchName || '-', style: 'detailHeading' }
                  : { text: this.previewData.vendorName || '-', style: 'detailHeading' },
                this.previewData.partyDocNo ? { text: `Party Doc No: ${this.previewData.partyDocNo}`, style: 'detailText' } : '',
                this.previewData.partyDocDate !== '-' ? { text: `Party Doc Date: ${this.previewData.partyDocDate}`, style: 'detailText' } : ''
              ].filter(Boolean),
              fillColor: '#f8fafc',
              margin: [8, 8, 8, 8]
            },
            {
              width: '*',
              stack: [
                { text: 'Transport & Challan', style: 'sectionHeader' },
                this.previewData.transporterMode ? { text: `Mode: ${this.previewData.transporterMode}`, style: 'detailText' } : '',
                this.previewData.transporterName ? { text: `Transporter: ${this.previewData.transporterName}`, style: 'detailText' } : '',
                this.previewData.transporterDocNo ? { text: `Transport Doc No: ${this.previewData.transporterDocNo}`, style: 'detailText' } : '',
                this.previewData.transporterDocDate !== '-' ? { text: `Transport Doc Date: ${this.previewData.transporterDocDate}`, style: 'detailText' } : '',
                this.previewData.vehicleNo ? { text: `Vehicle No: ${this.previewData.vehicleNo}`, style: 'detailText' } : '',
                this.previewData.challanDate !== '-' ? { text: `Challan Date: ${this.previewData.challanDate}`, style: 'detailText' } : ''
              ].filter(Boolean),
              fillColor: '#f8fafc',
              margin: [8, 8, 8, 8]
            }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 10]
        },
        this.previewData.sourceGroups.length > 0 ? {
          stack: [
            { text: 'Source Documents', style: 'sectionHeader' },
            {
              columns: this.previewData.sourceGroups.map((group: any) => ({
                width: '*',
                stack: [
                  { text: group.code || '-', bold: true },
                  { text: group.date || '-', color: '#64748b' },
                  { text: `Items: ${group.itemcount || (group.items?.length || 0)}`, color: '#475569', fontSize: 8.5 }
                ],
                fillColor: '#f8fafc',
                margin: [8, 8, 8, 8]
              })),
              columnGap: 8
            }
          ],
          margin: [0, 0, 0, 10]
        } : '',
        {
          table: {
            headerRows: 1,
            widths: [20, 58, '*', 30, 40, 40, 40, 40, 40, 54],
            body
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#16323f' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
            hLineColor: () => '#a7b5c8',
            vLineColor: () => '#a7b5c8'
          }
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                this.previewData.remarks !== '-' ? { text: `Remarks: ${this.previewData.remarks}`, style: 'detailText' } : '',
                { text: 'Amount In Words', style: 'sectionHeader', margin: [0, 10, 0, 2] },
                { text: this.previewData.amountInWords, style: 'detailHeading' }
              ].filter(Boolean)
            },
            {
              width: 200,
              stack: [
                { text: `For ${this.previewData.companyName}`, style: 'signatoryCompanyInline', alignment: 'right' },
                this.includeSignature && (this.previewData.stampImage || this.previewData.signatureImage)
                  ? {
                      columns: [
                        {
                          width: 72,
                          stack: [
                            this.previewData.stampImage
                              ? { image: this.previewData.stampImage, fit: [66, 66], opacity: 0.72, alignment: 'right', margin: [0, 4, -8, 0] }
                              : { text: '' }
                          ]
                        },
                        {
                          width: 118,
                          stack: [
                            this.previewData.signatureImage
                              ? { image: this.previewData.signatureImage, fit: [112, 38], alignment: 'right', margin: [0, 8, 0, 0] }
                              : { text: '' }
                          ]
                        }
                      ],
                      columnGap: -12,
                      margin: [0, 4, 0, 0]
                    }
                  : { text: '', margin: [0, 34, 0, 0] },
                this.previewData.signatoryName
                  ? { text: this.previewData.signatoryName, style: 'signatoryName', alignment: 'right', margin: [0, 4, 0, 0] }
                  : { text: '', margin: [0, 10, 0, 0] },
                { text: this.previewData.signatoryTitle || 'Authorized Signatory', style: 'signatoryRole', alignment: 'right' }
              ]
            }
          ],
          columnGap: 16,
          margin: [0, 12, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#11284c', alignment: 'center', margin: [0, 22, 0, 0] },
        companyHeader: { fontSize: 17.6, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.7, color: '#334155', alignment: 'center', margin: [0, 1, 0, 0] },
        badge: { fontSize: 11.5, bold: true, color: '#11284c', margin: [10, 10, 10, 7], alignment: 'left' },
        meta: { fontSize: 8.4, color: '#334155', margin: [10, 0, 10, 3], alignment: 'left' },
        summaryBox: { fontSize: 10, bold: true, color: '#0f172a', fillColor: '#f8fafc', margin: [8, 10, 8, 10] },
        sectionHeader: { fontSize: 9.6, bold: true, color: '#0f766e', margin: [0, 0, 0, 4] },
        detailHeading: { fontSize: 10.2, bold: true, color: '#11284c' },
        detailText: { fontSize: 8.9, color: '#334155', margin: [0, 1, 0, 0] },
        tableHeader: { fontSize: 8, bold: true, color: '#ffffff', alignment: 'center' },
        signatoryCompanyInline: { fontSize: 8.6, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryRole: { fontSize: 8.7, color: '#475569', bold: true },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: { fontSize: 9, color: '#0f172a' }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private extractGroups(source: any): any[] {
    const candidates = [
      source?.grnitemdetails,
      source?.grnItemDetails,
      source?.items,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        if (candidate.length && candidate[0]?.items) {
          return candidate.map((group: any) => ({
            code: this.safeValue(group.mtrcode || group.pocode || group.code, ''),
            date: this.formatDateValue(group.mtrdate || group.podate || group.date),
            itemcount: group.itemcount,
            items: Array.isArray(group.items) ? group.items : []
          }));
        }

        return [{
          code: '',
          date: '-',
          itemcount: candidate.length,
          items: candidate
        }];
      }
    }

    return [];
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website),
      this.metaLine('Phone', company.mobile || company.phone)
    ].filter(Boolean) as string[];
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    return normalized ? `${label}: ${normalized}` : '';
  }

  private joinAddress(parts: any[]): string {
    return parts.map((part) => this.normalizePart(part)).filter(Boolean).join(', ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `${String.fromCharCode(8377)} ${formatted}` : formatted;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'GR');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
